import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Loading, Popover } from '@moda/om';
import loadable from '@loadable/component';
import { hrefFor } from '../../routers';
import { useJustAddedItems } from './useJustAddedItems';
import { MiniCartLink } from './MiniCartLink';

type Props = {
  screen?: 'mobile' | 'desktop';
};

const MiniCartDropdown = loadable(() => import('./MiniCartDropdown'), {
  resolveComponent: exports => exports.MiniCartDropdown,
  fallback: <Loading />
});

export const MiniCart: React.FC<Props> = ({ screen = 'desktop' }) => {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const { justAddedItems, dismiss, persist } = useJustAddedItems();

  useEffect(() => {
    setShow(justAddedItems.length > 0);

    if (justAddedItems.length > 0) {
      setAnnouncement(`${justAddedItems[0].attributes.productName} added to bag`);
    }
  }, [justAddedItems, setShow]);

  const handleHide = useCallback(() => {
    setShow(false);
    dismiss();
  }, [setShow, dismiss]);

  const handleShow = useCallback(() => {
    setShow(true);
    persist();
  }, [setShow, persist]);

  if (location.pathname === hrefFor.ShoppingBagPage()) return <MiniCartLink />;

  return screen === 'desktop' ? (
    <>
      <div role="status" aria-live="polite" className="visually-hidden">
        {announcement}
      </div>
      <Popover
        open={show}
        anchor="topRight"
        role="dialog"
        aria-label="Shopping bag"
        popoverId="minicart-popover"
        onClose={handleHide}
        content={
          <MiniCartDropdown
            justAddedItems={justAddedItems}
            onHide={handleHide}
            onHover={handleShow}
          />
        }
      >
        <MiniCartLink isExpanded={show} />
      </Popover>
    </>
  ) : (
    <>
      <MiniCartLink />

      {show && (
        <div>
          <MiniCartDropdown
            justAddedItems={justAddedItems}
            onHide={handleHide}
            onHover={handleShow}
          />
        </div>
      )}
    </>
  );
};
